import { Input, InputRef, Modal } from 'antd';
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import * as St from './PasswordConfirmationDialog.styles';

import CommonButton from '~atoms/CommonButton';

interface PasswordModalProps {
  isVisible: boolean;
  onConfirm: (password: string) => void;
  onCancel: () => void;
}

const PasswordConfirmationDialog = ({
  isVisible,
  onConfirm,
  onCancel,
}: PasswordModalProps): JSX.Element => {
  const { t } = useTranslation();

  const [password, setPassword] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);

  const inputRef = useRef<InputRef>(null);
  const okButtonRef = useRef<HTMLButtonElement>(null);

  const isPasswordValid = useMemo(() => !!password?.trim(), [password]);

  const focusPasswordInput = useCallback(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleConfirm = useCallback(() => {
    focusPasswordInput();
    onConfirm(password || '');
  }, [password, onConfirm, focusPasswordInput]);

  const handleCancel = useCallback(() => {
    setPassword(undefined);
    onCancel();
  }, [onCancel]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.code === 'Enter' && isPasswordValid && okButtonRef.current) {
        okButtonRef.current.click();
      }
    },
    [isPasswordValid]
  );

  useEffect(() => {
    focusPasswordInput();
  }, [focusPasswordInput]);

  return (
    <Modal
      centered
      destroyOnClose
      open={isVisible}
      closable
      footer={null}
      keyboard
      onCancel={handleCancel}
      title={<div>{t('SCROLLABLE_AREA.PASSWORD.TITLE')}</div>}
    >
      <Input.Password
        ref={inputRef}
        value={password}
        placeholder="enter password"
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={handleKeyDown}
        type={showPassword ? 'text' : 'password'}
        iconRender={(visible) =>
          visible ? <EyeInvisibleOutlined /> : <EyeTwoTone />
        }
        title={t('COMMON.PASSWORD')}
        visibilityToggle={{
          visible: showPassword,
          onVisibleChange: setShowPassword,
        }}
      />

      <St.Container>
        <CommonButton onClick={handleCancel}>{t('COMMON.CLOSE')}</CommonButton>
        <CommonButton
          ref={okButtonRef}
          disabled={!isPasswordValid}
          onClick={handleConfirm}
        >
          {t('COMMON.SUBMIT')}
        </CommonButton>
      </St.Container>
    </Modal>
  );
};

export default PasswordConfirmationDialog;
